import React from 'react';
import { Bubble } from 'tea-component';
import { useIntl, getLocale } from 'umi';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { DEFAULT_LIST } from '@/pages/home/components/RegionSelect/contants';
import { DataSourceIcon } from '@/pages/home/pages/home/components/DataSource';
import './CountryRankCell.scss';

/** 从 DEFAULT_LIST 构建国家代码 → 英文全名映射 */
const COUNTRY_NAME_MAP: Record<string, string> = {};
DEFAULT_LIST.forEach((region) => {
  region.country_set?.forEach((c: any) => {
    if (c.abbre && c.name) {
      COUNTRY_NAME_MAP[c.abbre.toLowerCase()] = c.name;
    }
  });
});

const getCountryEnglishName = (code: string): string => {
  return COUNTRY_NAME_MAP[code?.toLowerCase()] || code?.toUpperCase() || '';
};

interface MarketItem {
  market: string;
  rank: number;
}

interface CountryRankCellProps {
  /** 国家数量 */
  count?: number;
  /** 国家缩写列表，如 ['CN', 'JP', 'US', 'KR'] */
  countries?: string[];
  /** 完整的 markets 数据（含 market + rank） */
  markets?: MarketItem[];
  /** 榜单名称，与列头 title 完全一致（已国际化） */
  chartLabel?: string;
  /** 榜单 icon，需与表头数据源 icon 保持一致 */
  chartIcon?: string;
}

const MAX_VISIBLE = 3;

export default function CountryRankCell(props: CountryRankCellProps) {
  const { count, countries = [], markets = [], chartLabel, chartIcon } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();

  if (!countries?.length && !count) {
    return <span>-</span>;
  }

  const getCountryDisplayName = (code: string): string => {
    const englishName = getCountryEnglishName(code);
    if (locale === LANGUAGE_CN) {
      return formatMessage({
        id: `dbh_country_${code?.toLowerCase()}`,
        defaultMessage: englishName,
      });
    }
    return englishName;
  };

  /** 根据 market code 查找对应的 rank */
  const getRank = (code: string): number | undefined => {
    const item = markets.find((m) => m.market?.toLowerCase() === code?.toLowerCase());
    return item?.rank;
  };

  /** 构造 hover 提示内容，前置国旗 icon */
  const getTooltip = (code: string): React.ReactNode => {
    const countryName = getCountryDisplayName(code);
    const rank = getRank(code);
    const tooltipText = chartLabel
      ? `${chartLabel} ${countryName}`
      : code?.toUpperCase();

    return (
      <div className="dbh-country-rank-cell__tooltip">
        {chartIcon ? <DataSourceIcon type={chartIcon} className="dbh-country-rank-cell__tooltip-chart-icon" /> : null}
        <img
          className="dbh-country-rank-cell__tooltip-flag"
          src={getCountryFlag(code)}
          alt={code}
        />
        <span className="dbh-country-rank-cell__tooltip-text">{tooltipText}</span>
        {rank != null ? <span className="dbh-country-rank-cell__tooltip-rank">#{rank}</span> : null}
      </div>
    );
  };

  const visibleCountries = countries.slice(0, MAX_VISIBLE);
  const extraCount = countries.length - MAX_VISIBLE;
  const extraCountries = countries.slice(MAX_VISIBLE);

  /** 可见国旗：带 Bubble hover 提示 */
  const renderFlag = (code: string) => {
    const tooltip = getTooltip(code);
    return (
      <Bubble key={code} content={tooltip}>
        <img
          className="dbh-country-rank-cell__flag"
          src={getCountryFlag(code)}
          alt={code}
        />
      </Bubble>
    );
  };

  /** +N 气泡：展开的国旗不带 hover */
  const renderExtraBubble = () => {
    if (extraCount <= 0) return null;
    return (
      <Bubble
        content={
          <div className="dbh-country-rank-cell__bubble">
            {extraCountries.map((code) => (
              <img
                key={code}
                className="dbh-country-rank-cell__bubble-flag"
                src={getCountryFlag(code)}
                alt={code}
              />
            ))}
          </div>
        }
      >
        <span className="dbh-country-rank-cell__extra">+{extraCount}</span>
      </Bubble>
    );
  };

  return (
    <div className="dbh-country-rank-cell">
      <div className="dbh-country-rank-cell__count">{count ?? countries.length}</div>
      <div className="dbh-country-rank-cell__flags">
        {visibleCountries.map(renderFlag)}
        {renderExtraBubble()}
      </div>
    </div>
  );
}
